import {
  ActivatedRouteSnapshot,
  ResolveFn,
  Route,
  RouterStateSnapshot,
} from '@angular/router';
import { marker as _ } from '@jsverse/transloco-keys-manager/marker';
import { hasScopesGuard, hasScopesResolver } from '@mds/ngx-auth';
import { ComponentDirtyCheckGuard } from '../../shared/guards/component-dirty-check.guard';
import {
  DriverResponse,
  ShiftResponse,
  VehicleResponse,
} from '../../shared/services/api-client';
import { driverResolverFactory } from '../driver/resolvers/driver.resolver';
import { assignableShiftsResolver } from '../shift/resolvers/assignable-shifts.resolver';
import { shiftResolverFactory } from '../shift/resolvers/shift.resolver';
import { vehicleResolverFactory } from '../vehicle/resolvers/vehicle.resolver';
import { startDateResolver } from './components/deployment-create/resolver/start-date.resolver';
import { canEditDeploymentGuard } from './components/deployment-edit/guards/can-edit-deployment.guard';
import { deploymentInstanceResolver } from './resolvers/deployment-instance.resolver';
import { deploymentResolver } from './resolvers/deployment.resolver';

export const deploymentRoutes: Route[] = [
  {
    path: '',
    title: _('i18n.Deployment.List.Title'),
    loadComponent: () =>
      import('./components/deployment-list/deployment-list.component').then(
        (m) => m.DeploymentListComponent,
      ),
    resolve: {
      assignableShifts: assignableShiftsResolver,
      hasWriteScope: hasScopesResolver(['deployments:write']),
      hasSeriesWriteScope: hasScopesResolver(['deploymentseries:write']),
    },
  },
  {
    path: 'create',
    title: _('i18n.Deployment.Create.Title'),
    loadComponent: () =>
      import('./components/deployment-create/deployment-create.component').then(
        (m) => m.DeploymentCreateComponent,
      ),
    canDeactivate: [ComponentDirtyCheckGuard],
    canActivate: [hasScopesGuard(['deployments:write', 'shifts:read'])],
    resolve: {
      assignableShifts: assignableShiftsResolver,
      presetStartDate: startDateResolver,
      presetDriver: (
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
      ): ReturnType<ResolveFn<DriverResponse | undefined>> => {
        const driverIdRaw = route.queryParamMap.get('driverId');
        const driverId = Number(driverIdRaw);

        if (!driverIdRaw) {
          return undefined;
        }

        const driverResolver = driverResolverFactory(driverId);
        return driverResolver(route, state);
      },
      presetVehicle: (
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
      ): ReturnType<ResolveFn<VehicleResponse | undefined>> => {
        const vehicleIdRaw = route.queryParamMap.get('vehicleId');
        const vehicleId = Number(vehicleIdRaw);

        if (!vehicleIdRaw) {
          return undefined;
        }

        const vehicleResolver = vehicleResolverFactory(vehicleId);
        return vehicleResolver(route, state);
      },
      presetShift: (
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
      ): ReturnType<ResolveFn<ShiftResponse | undefined>> => {
        const shiftIdRaw = route.queryParamMap.get('shiftId');
        const shiftId = Number(shiftIdRaw);

        if (!shiftIdRaw) {
          return undefined;
        }

        const shiftResolver = shiftResolverFactory(shiftId);
        return shiftResolver(route, state);
      },
      hasSeriesWriteScope: hasScopesResolver(['deploymentseries:write']),
    },
  },
  {
    path: ':deploymentId',
    children: [
      {
        path: 'detail',
        resolve: {
          deployment: deploymentResolver,
          hasWriteScope: hasScopesResolver(['deployments:write']),
          hasSeriesWriteScope: hasScopesResolver(['deploymentseries:write']),
        },
        loadComponent: () =>
          import('./components/deployment-detail/deployment-detail.component').then(
            (m) => m.DeploymentDetailComponent,
          ),
      },
      {
        path: 'edit',
        resolve: {
          deployment: deploymentResolver,
          assignableShifts: assignableShiftsResolver,
        },
        loadComponent: () =>
          import('./components/deployment-edit/deployment-edit.component').then(
            (m) => m.DeploymentEditComponent,
          ),
        canDeactivate: [ComponentDirtyCheckGuard],
        canActivate: [
          hasScopesGuard(['deployments:write']),
          canEditDeploymentGuard,
        ],
      },
    ],
  },
  {
    path: ':deploymentId/instances/:deploymentInstanceId',
    children: [
      {
        path: 'detail',
        resolve: {
          deploymentInstance: deploymentInstanceResolver,
          hasWriteScope: hasScopesResolver(['deployments:write']),
        },
        loadComponent: () =>
          import('./components/deployment-instance-detail/deployment-instance-detail.component').then(
            (m) => m.DeploymentInstanceDetailComponent,
          ),
      },
      {
        path: 'edit',
        resolve: {
          deploymentInstance: deploymentInstanceResolver,
        },
        loadComponent: () =>
          import('./components/deployment-instance-edit/deployment-instance-edit.component').then(
            (m) => m.DeploymentInstanceEditComponent,
          ),
        canDeactivate: [ComponentDirtyCheckGuard],
        canActivate: [hasScopesGuard(['deployments:write'])],
      },
    ],
  },
];
